import { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import PhotoGrid from '../shared/PhotoGrid';
import FullScreenView from '../shared/FullScreenView';
import { formatExpiry } from '../../utils/formatTime';
import type { PhotoLike } from '../../types';

type SecondaryBatch = {
  id: number;
  galleryName: string;
  year: string;
  status: string;
  createdAt: string;
  windowEndsAt: string | null;
  parentBatches: { id: number; galleryName: string; year: string }[];
  assignedAd: { fName: string; lName: string } | null;
  assignedAdId: number | null;
  adNotified: boolean;
};

type ReviewRequest = {
  photoId: number;
  requesters: { id: number; fName: string; lName: string }[];
};

type Props = {
  batchId: string;
  onBack: () => void;
};

export default function SecondaryBatchDetail({ batchId, onBack }: Props) {
  const [batchInfo, setBatchInfo] = useState<SecondaryBatch | null>(null);
  const [photos, setPhotos] = useState<PhotoLike[]>([]);
  const [requesterMap, setRequesterMap] = useState<Map<number, { id: number; fName: string; lName: string }[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const [notifying, setNotifying] = useState(false);
  const [notifyError, setNotifyError] = useState('');
  const [adNotified, setAdNotified] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [batchRes, photosRes, requestsRes] = await Promise.all([
        apiFetch(`/api/batches/${batchId}`, { headers: authHeaders() }),
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() }),
        apiFetch(`/api/batches/${batchId}/review-requests`, { headers: authHeaders() }),
      ]);

      if (!batchRes.ok) throw new Error('Failed to load batch');
      const batchData: SecondaryBatch = await batchRes.json();
      setBatchInfo(batchData);
      setAdNotified(batchData.adNotified);

      const photosData = photosRes.ok ? await photosRes.json() : [];
      setPhotos(Array.isArray(photosData) ? photosData : []);

      const requestsData: ReviewRequest[] = requestsRes.ok ? await requestsRes.json() : [];
      const map = new Map<number, { id: number; fName: string; lName: string }[]>();
      for (const req of requestsData) map.set(req.photoId, req.requesters);
      setRequesterMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleNotify = async () => {
    if (!batchInfo?.assignedAdId) return;
    setNotifying(true);
    setNotifyError('');
    try {
      const res = await apiFetch(`/api/batches/${batchId}/notify-ad`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        setAdNotified(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setNotifyError((data as { message?: string }).message || 'Failed to send notification');
      }
    } catch {
      setNotifyError('Failed to send notification');
    } finally {
      setNotifying(false);
    }
  };

  const activePhoto = activePhotoIndex !== null ? photos[activePhotoIndex] ?? null : null;
  const requestedCount = photos.filter(p => (requesterMap.get(p.id)?.length ?? 0) > 0).length;
  const windowClosed = batchInfo?.status === 'uploaded';

  return (
    <div className='secondary-detail'>
      <div className='secondary-detail-header'>
        <button className='back-link' onClick={onBack}>← Batches</button>
        {batchInfo && (
          <span className='photos-batch-info'>
            {' '}{batchInfo.galleryName} · {batchInfo.year}{' '}
          </span>
        )}
        <span className={`batch-status-badge ${windowClosed ? 'uploaded' : 'review-open'}`}>
          {windowClosed ? 'Window Closed' : 'Review Open'}
        </span>
      </div>

      {windowClosed && batchInfo?.assignedAdId && !adNotified && (
        <div className='upload-notify-bar'>
          <span>
            {batchInfo.assignedAd
              ? `${batchInfo.assignedAd.fName} ${batchInfo.assignedAd.lName} needs to be notified — the review window has closed and the Artist's requests are ready for selection.`
              : 'Artistic Staff needs to be notified — the review window has closed.'}
          </span>
          <button className='app-nav-btn' onClick={handleNotify} disabled={notifying}>
            {notifying ? 'Sending…' : `Notify ${batchInfo.assignedAd?.fName ?? 'Artistic Staff'}`}
          </button>
          {notifyError && <span className='upload-notify-error'>{notifyError}</span>}
        </div>
      )}

      {windowClosed && adNotified && (
        <p className='upload-status-banner'>
          {batchInfo?.assignedAd
            ? `${batchInfo.assignedAd.fName} ${batchInfo.assignedAd.lName} has been notified`
            : 'Artistic Staff notified'} — awaiting their photo selections.
        </p>
      )}

      {batchInfo && (
        <div className='secondary-detail-meta'>
          <div className='secondary-detail-times'>
            <span>Opened {formatExpiry(batchInfo.createdAt)}</span>
            {batchInfo.windowEndsAt && (
              <span className='secondary-detail-closes'>
                {windowClosed ? 'Closed' : 'Closes'} {formatExpiry(batchInfo.windowEndsAt)}
              </span>
            )}
          </div>
          {batchInfo.parentBatches.length > 0 && (
            <p className='secondary-detail-meta-row'>
              <strong>Batches:</strong>{' '}
              {batchInfo.parentBatches.map(b => `${b.galleryName} — ${b.year}`).join(', ')}
            </p>
          )}
          {batchInfo.assignedAd && (
            <p className='secondary-detail-meta-row'>
              <strong>Artistic Staff:</strong>{' '}
              {batchInfo.assignedAd.fName} {batchInfo.assignedAd.lName}
            </p>
          )}
        </div>
      )}

      <div className='secondary-detail-toolbar'>
        <p className='secondary-detail-summary'>
          {loading ? 'Loading…' : (
            <>
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
              {requestedCount > 0 && (
                <> · <strong>{requestedCount}</strong> requested</>
              )}
            </>
          )}
        </p>
        <button
          className='app-nav-btn app-nav-btn--secondary'
          onClick={fetchAll}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <p className='secondary-detail-error'>{error}</p>}

      {!loading && photos.length === 0 && !error && (
        <p className='batch-list-empty'>No photos in this window.</p>
      )}

      {photos.length > 0 && (
        <PhotoGrid
          photos={photos}
          onSelect={setActivePhotoIndex}
          renderCaption={(photo) => {
            const requesters = requesterMap.get(photo.id);
            if (!requesters || requesters.length === 0) return null;
            return (
              <div className='secondary-detail-caption'>
                {requesters.map(r => `${r.fName} ${r.lName}`).join(', ')}
              </div>
            );
          }}
        />
      )}

      <FullScreenView
        photo={activePhoto}
        onClose={() => setActivePhotoIndex(null)}
        onPrev={() => activePhotoIndex !== null && activePhotoIndex > 0 && setActivePhotoIndex(activePhotoIndex - 1)}
        onNext={() => activePhotoIndex !== null && activePhotoIndex < photos.length - 1 && setActivePhotoIndex(activePhotoIndex + 1)}
        hasPrev={activePhotoIndex !== null && activePhotoIndex > 0}
        hasNext={activePhotoIndex !== null && activePhotoIndex < photos.length - 1}
      />
    </div>
  );
}